import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { ApiError } from '../api/client';
import { authApi } from '../api/endpoints';
import { useAuth } from '../app/AuthProvider';
import { useToast } from '../app/ToastProvider';
import { AuthShell } from './AuthShell';

export function FirstLoginPasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authApi.changePassword(currentPassword, newPassword, confirmPassword);
      await refreshUser();
      toast.success('Your password has been updated.');
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Your password could not be updated.', 0));
    } finally {
      setSubmitting(false);
    }
  };

  const fields = error?.byField() ?? {};

  return (
    <AuthShell title="Set your new password" subtitle="Replace the temporary password before continuing to the CRM.">
      <form onSubmit={submit}>
        <Stack spacing={2.5}>
          {error ? <Alert severity="error">{error.message}</Alert> : null}
          <TextField
            label="Temporary password"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            error={Boolean(fields.currentPassword)}
            helperText={fields.currentPassword}
            autoComplete="current-password"
            required
            autoFocus
          />
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            error={Boolean(fields.newPassword)}
            helperText={fields.newPassword ?? 'Use at least 8 characters.'}
            slotProps={{ htmlInput: { minLength: 8, maxLength: 128 } }}
            autoComplete="new-password"
            required
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            error={Boolean(fields.confirmPassword)}
            helperText={fields.confirmPassword}
            autoComplete="new-password"
            required
          />
          <Button type="submit" variant="contained" size="large" disabled={submitting}>
            {submitting ? 'Updating password...' : 'Set Password'}
          </Button>
          <Button color="inherit" onClick={() => void logout()} disabled={submitting}>
            Sign out
          </Button>
        </Stack>
      </form>
    </AuthShell>
  );
}
